<template>
  <PluginMenuButton
    id="publish"
    :title="publishLabel"
    :description="publishDescription"
    :disabled="!mutations.length || !canEdit"
    type="success"
    :weight="0"
    :icon="icon"
    @click="onMenuClick"
  />
  <Teleport to="body">
    <transition appear name="bk-slide-up">
      <PublishDialog
        v-if="showDialog"
        v-model:states="additionalEditStates"
        v-model:revision-message="revisionMessage"
        v-model:should-publish="shouldPublish"
        @close="showDialog = false"
        @submit="onSubmit"
      />
    </transition>
  </Teleport>
</template>

<script setup>
import {
  useBlokkli,
  defineBlokkliFeature,
  computed,
  useRoute,
  ref
} from "#imports";
import { PluginMenuButton } from "#blokkli/plugins";
import PublishDialog from "./Dialog/index.vue";
const { adapter, settings } = defineBlokkliFeature({
  id: "publish",
  icon: "publish",
  label: "Publish",
  requiredAdapterMethods: ["publish"],
  description: "Provides a menu button to publish the changes of the current entity.",
  settings: {
    closeAfterPublish: {
      type: "checkbox",
      label: "Close editor after publishing",
      description: "Immediately closes the editor after successfully publishing or saving.",
      default: true,
      group: "behavior"
    }
  }
});
const route = useRoute();
const { state, $t, broadcast, context, eventBus } = useBlokkli();
const { mutations, canEdit, mutateWithLoadingState } = state;
const hasPublishOptions = !!adapter.getPublishOptions;
const isPublished = computed(() => !!state.entity.value.status);
const showDialog = ref(false);
const additionalEditStates = ref([]);
const shouldPublish = ref(!!state.entity.value.status);
const revisionMessage = ref("");
const publishLabel = computed(() => {
  const suffix = hasPublishOptions ? "..." : "";
  if (isPublished.value) {
    return (settings.value.closeAfterPublish ? $t("publishAndCloseLabel", "Publish & Close") : $t("publishLabel", "Publish")) + suffix;
  }
  return (settings.value.closeAfterPublish ? $t("publishAndCloseLabelUnpublished", "Save & Close") : $t("publishLabelUnpublished", "Save")) + suffix;
});
const publishDescription = computed(
  () => isPublished.value ? $t("publishDescription", "Publish all changes.") : $t(
    "publishDescriptionUnpublished",
    "Save all changes while keeping page unpublished"
  )
);
const icon = computed(
  () => isPublished.value ? "publish" : "save"
);
const onMenuClick = async () => {
  if (hasPublishOptions) {
    showDialog.value = true;
    return;
  }
  await publishCurrent();
};
async function publishCurrent() {
  const success = await mutateWithLoadingState(
    () => adapter.publish({
      hostEntityType: context.value.entityType,
      hostEntityUuid: context.value.entityUuid,
      closeAfterPublish: settings.value.closeAfterPublish
    }),
    $t("publishError", "Changes could not be published."),
    $t("publishSuccess", "Changes published successfully.")
  );
  if (!success) {
    const validations = state.violations.value;
    if (validations.length) {
      eventBus.emit("publish:failed");
      eventBus.emit("sidebar:open", "violations");
    }
    return;
  }
  onSubmit();
}
function onSubmit() {
  broadcast.emit("published", { uuid: context.value.entityUuid });
  if (settings.value.closeAfterPublish) {
    window.location.href = route.path;
  }
}
</script>

<script>
export default {
  name: "Publish"
};
</script>
